import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import { useState } from "react";
import ChecklistItemService, {
    ChecklistItemCreateParams,
    ChecklistItemPathParams,
    ChecklistItemUpdateParams,
} from "utilities/services/block-configurations/checklistitems/checklist-item-service";

interface UseChecklistItem {
    createChecklistItem: (
        configurationId: number,
        precedingItemId?: number,
        text?: string
    ) => Promise<ChecklistItemRecord | undefined>;
    deleteChecklistItem: (checklistitemId: number) => Promise<boolean>;
    isSaving: boolean;
    updateChecklistItem: (
        checklistItem: ChecklistItemRecord
    ) => Promise<ChecklistItemRecord | undefined>;
}

const useChecklistItem = (): UseChecklistItem => {
    const [isSaving, setIsSaving] = useState(false);
    const { create } = ChecklistItemService.useCreate();
    const { update } = ChecklistItemService.useUpdate();
    const { delete: deleteChecklistItemApi } = ChecklistItemService.useDelete();

    const deleteChecklistItem = async (
        checklistitemId: number
    ): Promise<boolean> => {
        setIsSaving(true);

        try {
            const response = await deleteChecklistItemApi(checklistitemId);

            if (
                response?.result == null ||
                response.resultObject?.valueOf() !== true
            ) {
                throw new Error(
                    `Failed to delete checklist item with ID ${checklistitemId}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return true;
        } catch (error) {
            console.error(error);

            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const createChecklistItem = async (
        configurationId: number,
        precedingItemId?: number,
        text?: string
    ): Promise<ChecklistItemRecord | undefined> => {
        if (configurationId <= 0) {
            return;
        }

        setIsSaving(true);

        try {
            const requestData: ChecklistItemCreateParams = {
                configurationId: configurationId,
                precedingItemId: precedingItemId,
                text: text,
            };

            const response = await create(requestData);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `No result returned when creating checklist item on checklist with ID ${configurationId}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            console.error(error);

            return undefined;
        } finally {
            setIsSaving(false);
        }
    };

    const updateChecklistItem = async (
        checklistitem: ChecklistItemRecord
    ): Promise<ChecklistItemRecord | undefined> => {
        if (checklistitem?.id == null) {
            return;
        }

        setIsSaving(true);

        try {
            const requestData: ChecklistItemUpdateParams = checklistitem.toJS();
            const pathParams: ChecklistItemPathParams = {
                id: checklistitem.id,
            };

            const response = await update(requestData, pathParams);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `No result returned when updating checklist item with ID ${checklistitem.id}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            console.error(error);

            return undefined;
        } finally {
            setIsSaving(false);
        }
    };

    return {
        createChecklistItem,
        deleteChecklistItem,
        isSaving,
        updateChecklistItem,
    };
};
export default useChecklistItem;
